import { computed } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useStore } from 'dashboard/composables/store';

/**
 * Composable for building account-scoped frontend URLs
 *
 * This composable simplifies the creation of URLs that include the current account ID,
 * eliminating the need to manually import frontendURL, useStore, and computed in every component.
 *
 * @returns {Object} An object containing the accountUrl function
 *
 * @example
 * // Basic usage
 * const { accountUrl } = useFrontendUrl();
 * const settingsRoute = accountUrl('settings');
 * // Returns computed ref: /app/accounts/123/settings
 *
 * @example
 * // With query parameters
 * const { accountUrl } = useFrontendUrl();
 * const conversationsRoute = accountUrl('conversations', { status: 'open' });
 * // Returns computed ref: /app/accounts/123/conversations?status=open
 */
export const useFrontendUrl = () => {
  const store = useStore();
  const accountId = computed(() => store.getters.getCurrentAccountId);

  /**
   * Build an account-scoped URL
   *
   * @param {string} path - The path relative to the account (e.g., 'ai/actions', 'settings')
   * @param {Object} [params] - Optional query parameters to append to the URL
   * @returns {import('vue').ComputedRef<string>} A computed ref containing the full frontend URL
   */
  const accountUrl = (path, params) => {
    return computed(() =>
      frontendURL(`accounts/${accountId.value}/${path}`, params)
    );
  };

  return { accountUrl };
};
